import React, { useCallback, useState, useRef, useEffect } from "react";
import { connect, useDispatch } from "react-redux";

import { actions } from "store/users";
import { resetForm, initForm } from "store/form/actions";

import { useStateSelector } from "utils/field";
import { getOptions } from "utils/fields";
import { maxLength, personName, companyName } from "utils/fieldValidation";

import Field from "components/Field";
import SectionHeading from "components/SectionHeading";
import InputRow from "components/Input/Row";
import Input from "components/Input";
import Select from "components/Input/Select";
import Button from "components/Button";

import classes from "./style.module.scss";

const charLim200 = maxLength(200);
const charLimPostalCode = maxLength(7);

const { addUserPreferences } = actions;

// newForm.userPreferences

const Preferences = ({ initialValues, uid }) => {
    const dispatch = useDispatch();

    const {
        firstName = "",
        lastName = "",
        company = "",
        userPhone = "",
        eaId = "",
        companyPhone = "",
        province = "",
        preferredUnits = "",
        preferredProgramType = "",
        companyAddress = "",
        companyCity = "",
        companyPostalCode = "",
        companyFax = "",
    } = useStateSelector("newForm.userPreferences") || {};

    const [dataChanged, setDataChanged] = useState(false);
    const [submitBtnLabel, setSubmitBtnLabel] = useState("Save Changes");

    useEffect(() => {
        dispatch(initForm("userPreferences", initialValues));

        () => {
            dispatch(resetForm("userPreferences"));
        };
    }, []);

    const handleSave = () => {
        handleSubmit();
        setSubmitBtnLabel("Saving...");
        setTimeout(() => {
            setSubmitBtnLabel("Saved");
        }, 500);
        setDataChanged(false);
    };

    const inputRef = useRef("");

    const handleChange = useCallback(
        (fieldName, value) => {
            if (value !== initialValues[fieldName]) {
                setDataChanged(true);
                setSubmitBtnLabel("Save Changes");
            }
        },
        [initialValues]
    );

    const handleSubmit = async () => {
        const userMeta = {
            company,
        };

        const userPreferences = {
            userPhone,
            eaId,
            companyPhone,
            province,
            preferredUnits,
            preferredProgramType,
            companyAddress,
            companyCity,
            companyPostalCode,
            companyFax,
        };

        const name = `${firstName} ${lastName}`;

        return dispatch(addUserPreferences({ uid, name, userMeta, userPreferences }));
    };

    return (
        <div>
            <SectionHeading
                title="User Preferences"
                subtitle="Enter values to be automatically applied to all new models"
            />
            <div className={classes.contentWrapper}>
                <InputRow gridTemplate="1fr 1fr">
                    <Field
                        component={Input}
                        name="newForm.userPreferences.firstName"
                        type="text"
                        label="First Name"
                        ref={inputRef}
                        validate={[charLim200, personName]}
                        onChange={handleChange.bind(this, "firstName")}
                    />
                    <Field
                        component={Input}
                        name="newForm.userPreferences.lastName"
                        type="text"
                        label="Last Name"
                        validate={[charLim200, personName]}
                        onChange={handleChange.bind(this, "lastName")}
                    />
                    <Field
                        component={Input}
                        name="newForm.userPreferences.userPhone"
                        type="text"
                        label="Personal Phone"
                        validate={[charLim200]}
                        onChange={handleChange.bind(this, "userPhone")}
                    />
                    <Field
                        component={Input}
                        name="newForm.userPreferences.eaId"
                        type="text"
                        label="EA ID"
                        validate={[charLim200]}
                        onChange={handleChange.bind(this, "eaId")}
                    />
                    <Field
                        component={Input}
                        name="newForm.userPreferences.company"
                        type="text"
                        label="User Company"
                        validate={[charLim200, companyName]}
                        onChange={handleChange.bind(this, "company")}
                    />
                </InputRow>
                <InputRow gridTemplate="1fr 1fr">
                    <Field
                        component={Input}
                        name="newForm.userPreferences.companyCity"
                        type="text"
                        label="Company City"
                        validate={[charLim200]}
                        onChange={handleChange.bind(this, "companyCity")}
                    />
                    <Field
                        component={Select}
                        name="newForm.userPreferences.province"
                        type="text"
                        label="Province"
                        options={getOptions({ fieldKey: "weatherRegion", version: "v11-11" })}
                        validate={[charLim200]}
                        onChange={handleChange.bind(this, "province")}
                    />
                    <Field
                        component={Input}
                        name="newForm.userPreferences.companyAddress"
                        type="text"
                        label="Company Street Address"
                        validate={[charLim200]}
                        onChange={handleChange.bind(this, "companyAddress")}
                    />
                    <Field
                        component={Input}
                        name="newForm.userPreferences.companyPostalCode"
                        type="text"
                        label="Company Postal Code"
                        validate={[charLimPostalCode]}
                        onChange={handleChange.bind(this, "companyPostalCode")}
                    />
                    <Field
                        component={Input}
                        name="newForm.userPreferences.companyPhone"
                        type="text"
                        label="Company Phone"
                        validate={[charLim200]}
                        onChange={handleChange.bind(this, "companyPhone")}
                    />
                    <Field
                        component={Input}
                        name="newForm.userPreferences.companyFax"
                        type="text"
                        label="Company Fax"
                        validate={[charLim200]}
                        onChange={handleChange.bind(this, "companyFax")}
                    />
                </InputRow>

                <InputRow gridTemplate="1fr 1fr">
                    <Field
                        component={Select}
                        name="newForm.userPreferences.preferredUnits"
                        type="text"
                        label="Primary Display Units"
                        options={[
                            {
                                label: "Metric",
                                value: "Metric",
                            },
                            {
                                label: "Imperial",
                                value: "Imperial",
                            },
                            {
                                label: "US",
                                value: "US",
                            },
                        ]}
                        validate={[charLim200]}
                        onChange={handleChange.bind(this, "preferredUnits")}
                    />
                    <Field
                        component={Select}
                        name="newForm.userPreferences.preferredProgramType"
                        options={[
                            {
                                label: "General",
                                value: "",
                            },
                            {
                                label: "EnerGuide Rating System",
                                value: "ca.nrcan.gc.OEE.ERS.ErsProgram",
                            },
                            {
                                label: "Ontario Reference House",
                                value: "ca.nrcan.gc.OEE.ONrh.OnProgram",
                            },
                        ]}
                        label="Program Type"
                        onChange={handleChange.bind(this, "preferredProgramType")}
                    />
                </InputRow>
                <InputRow gridTemplate="1fr 1fr">
                    <Button
                        style={{ marginTop: "1.1rem" }}
                        onClick={() => {
                            handleSave();
                        }}
                        disabled={!dataChanged}
                    >
                        {submitBtnLabel}
                    </Button>
                </InputRow>
            </div>
        </div>
    );
};

const mapStateToProps = ({
    user: {
        uid,
        name = "",
        // email='', //field would have to be disabled
        userMeta: { company = "", role = "", experienceLevel = "", agreedToTerms = false } = {},
        userPreferences: {
            userPhone = "",
            eaId = "",
            companyPhone = "",
            province = "",
            preferredUnits = "",
            preferredProgramType = "",
            companyAddress = "",
            companyCity = "",
            companyPostalCode = "",
            companyFax = "",
        } = {},
    },
}) => {
    const nameArray = name ? name.split(" ") : [];
    const initialValues = name
        ? {
              firstName: nameArray[0],
              lastName: nameArray[1],
              userPhone,
              eaId,
              company,
              companyPhone,
              province,
              preferredUnits,
              preferredProgramType,
              companyAddress,
              companyCity,
              companyPostalCode,
              companyFax,
          }
        : {};

    return {
        uid,
        initialValues: {
            ...initialValues,
        },
    };
};

export default connect(mapStateToProps, null)(Preferences);
